<script setup lang="ts">
import type { Align, Side } from 'node_modules/reka-ui/dist/Popper'
import { TooltipArrow, TooltipContent, type TooltipContentEmits, type TooltipContentProps, TooltipPortal, useForwardPropsEmits } from 'reka-ui'
import { computed, type HTMLAttributes } from 'vue'

import { cn } from '../../../lib/utils'

defineOptions({
  inheritAttrs: false,
})

export type ATooltipContentProperties = TooltipContentProps & {
  class?: HTMLAttributes['class']
  align?: Align
  side?: Side
  hideWhenDetached?: boolean
}

const properties = withDefaults(defineProps<ATooltipContentProperties>(), {
  class: undefined,
  side: 'top',
  align: 'center',
  hideWhenDetached: undefined,
})

const emits = defineEmits<TooltipContentEmits>()

const delegatedProperties = computed(() => {
  const { class: _, ...delegated } = properties

  return delegated
})

const forwarded = useForwardPropsEmits(delegatedProperties, emits)
</script>

<template>
  <TooltipPortal>
    <TooltipContent v-bind="{ ...forwarded, ...$attrs }" :align :class="cn('atabix-tooltip--content', properties.class)" :hide-when-detached :side>
      <TooltipArrow class="atabix-tooltip--arrow" :height="8" :width="16" />
      <slot />
    </TooltipContent>
  </TooltipPortal>
</template>

<style lang="scss">
.atabix-tooltip--content {
  @apply relative;
  @apply z-50;
  @apply rounded-md;
  @apply bg-tooltip-background;
  @apply px-3;
  @apply py-1.5;
  @apply text-sm;
  @apply text-tooltip-foreground;
  @apply shadow-md;
  @apply animate-in;
  @apply fade-in-0;
  @apply zoom-in-95;
  @apply data-[state=closed]:animate-out;
  @apply data-[state=closed]:fade-out-0;
  @apply data-[state=closed]:zoom-out-95;
  @apply data-[side=bottom]:slide-in-from-top-2;
  @apply data-[side=left]:slide-in-from-right-2;
  @apply data-[side=right]:slide-in-from-left-2;
  @apply data-[side=top]:slide-in-from-bottom-2;

  // Fix built-in misalignment. This is only ever really noticeable when the tooltip is around a very small trigger.
  @apply data-[side=bottom]:translate-x-[-1px];
  @apply data-[side=top]:translate-x-[-1px];
}

.atabix-tooltip--arrow {
  @apply fill-tooltip-background;
}
</style>
